// Korean-photobooth style presets: stage backgrounds, frame layouts, frame
// colours and colour filters. Everything is procedural (no image assets), so it
// is tiny, works offline and renders identically on every device.

export type BackgroundId =
  | 'studio-white' | 'pastel-pink' | 'baby-blue' | 'butter' | 'lavender'
  | 'mint-check' | 'pink-check' | 'hearts' | 'stars' | 'sunset' | 'film-black'
  /** seasonal packs (see PACKS) – stay renderable after the pack expires */
  | 'halloween-moon' | 'xmas-snow' | 'tet-blossom' | 'valentine-letters'
  /** an uploaded / linked / catalog image */
  | 'custom'
  /** a member's real surroundings via their camera (see BoothConfig.sceneSlot) */
  | 'member-camera'
  /** legacy alias of member-camera with slot 0 */
  | 'host-camera';

export interface Background {
  id: BackgroundId;
  name: string;
  swatch: string;
  /** seasonal pack this belongs to */
  pack?: PackId;
}

export const BACKGROUNDS: Background[] = [
  { id: 'studio-white', name: 'Studio trắng', swatch: '#f4f1ec' },
  { id: 'pastel-pink', name: 'Hồng pastel', swatch: '#f9d5dd' },
  { id: 'baby-blue', name: 'Xanh baby', swatch: '#cfe3f7' },
  { id: 'butter', name: 'Vàng bơ', swatch: '#fbefc2' },
  { id: 'lavender', name: 'Tím lavender', swatch: '#e3d9f6' },
  { id: 'mint-check', name: 'Caro mint', swatch: '#cdeee0' },
  { id: 'pink-check', name: 'Caro hồng', swatch: '#ffd6e4' },
  { id: 'hearts', name: 'Trái tim', swatch: '#ffe3ea' },
  { id: 'stars', name: 'Ngôi sao', swatch: '#1f2550' },
  { id: 'sunset', name: 'Hoàng hôn', swatch: '#ffb38a' },
  { id: 'film-black', name: 'Film đen', swatch: '#151515' },
];

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function check(ctx: Ctx, w: number, h: number, a: string, b: string) {
  ctx.fillStyle = a;
  ctx.fillRect(0, 0, w, h);
  const s = Math.round(Math.min(w, h) / 9);
  ctx.fillStyle = b;
  for (let y = 0; y < h; y += s)
    for (let x = (y / s) % 2 ? s : 0; x < w; x += 2 * s) ctx.fillRect(x, y, s, s);
}

export function heart(ctx: Ctx, x: number, y: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x, y + r * 0.35);
  ctx.bezierCurveTo(x, y - r * 0.3, x - r, y - r * 0.3, x - r, y + r * 0.25);
  ctx.bezierCurveTo(x - r, y + r * 0.75, x, y + r * 1.05, x, y + r * 1.3);
  ctx.bezierCurveTo(x, y + r * 1.05, x + r, y + r * 0.75, x + r, y + r * 0.25);
  ctx.bezierCurveTo(x + r, y - r * 0.3, x, y - r * 0.3, x, y + r * 0.35);
  ctx.fill();
}

export function star(ctx: Ctx, x: number, y: number, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = (Math.PI / 5) * i - Math.PI / 2;
    const rr = i % 2 ? r * 0.45 : r;
    ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fill();
}

/** Deterministic PRNG so both devices draw the exact same pattern. */
function rng(seed: number) {
  return () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
}

function softGradient(ctx: Ctx, w: number, h: number, top: string, bottom: string) {
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  // studio vignette / floor glow
  const r = ctx.createRadialGradient(w / 2, h * 0.45, h * 0.1, w / 2, h * 0.5, h * 0.9);
  r.addColorStop(0, 'rgba(255,255,255,0.35)');
  r.addColorStop(1, 'rgba(0,0,0,0.06)');
  ctx.fillStyle = r;
  ctx.fillRect(0, 0, w, h);
}

export function drawBackground(ctx: Ctx, id: BackgroundId, w: number, h: number, custom?: CanvasImageSource | null) {
  const rand = rng(42);
  switch (id) {
    case 'studio-white': softGradient(ctx, w, h, '#fbfaf7', '#e7e2da'); break;
    case 'pastel-pink': softGradient(ctx, w, h, '#fde6eb', '#f4c3cf'); break;
    case 'baby-blue': softGradient(ctx, w, h, '#e6f1fb', '#b9d5f0'); break;
    case 'butter': softGradient(ctx, w, h, '#fff8dc', '#f6e3a1'); break;
    case 'lavender': softGradient(ctx, w, h, '#f1ebfb', '#d5c6f0'); break;
    case 'mint-check': check(ctx, w, h, '#e3f6ee', '#c4eadb'); break;
    case 'pink-check': check(ctx, w, h, '#fff0f5', '#ffd3e2'); break;
    case 'hearts': {
      softGradient(ctx, w, h, '#fff1f4', '#ffd9e2');
      ctx.fillStyle = 'rgba(255,120,150,0.35)';
      for (let i = 0; i < 26; i++) heart(ctx, rand() * w, rand() * h, (0.02 + rand() * 0.035) * w);
      break;
    }
    case 'stars': {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, '#141a3d');
      g.addColorStop(1, '#3b2a6b');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < 60; i++) {
        ctx.fillStyle = `rgba(255,${230 + rand() * 25},${180 + rand() * 60},${0.4 + rand() * 0.6})`;
        star(ctx, rand() * w, rand() * h, (0.004 + rand() * 0.014) * w);
      }
      break;
    }
    case 'sunset': {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, '#ffd1a1');
      g.addColorStop(0.55, '#ff9e9e');
      g.addColorStop(1, '#a98bd8');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      break;
    }
    case 'film-black': softGradient(ctx, w, h, '#2a2a2a', '#0c0c0c'); break;
    case 'halloween-moon': {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, '#2a1846');
      g.addColorStop(1, '#5b2a5e');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(255,236,170,0.95)';
      ctx.beginPath();
      ctx.arc(w * 0.8, h * 0.2, h * 0.11, 0, Math.PI * 2);
      ctx.fill();
      for (let i = 0; i < 30; i++) {
        ctx.fillStyle = `rgba(255,240,200,${0.3 + rand() * 0.5})`;
        star(ctx, rand() * w, rand() * h * 0.6, (0.003 + rand() * 0.007) * w);
      }
      ctx.fillStyle = 'rgba(20,10,30,0.85)';
      for (let i = 0; i < 6; i++) bat(ctx, w * (0.1 + rand() * 0.8), h * (0.08 + rand() * 0.35), w * (0.025 + rand() * 0.025));
      ctx.fillStyle = '#ff8a2a';
      for (let i = 0; i < 4; i++) pumpkin(ctx, w * (0.08 + i * 0.28 + rand() * 0.05), h * 0.93, w * (0.035 + rand() * 0.02));
      break;
    }
    case 'xmas-snow': {
      softGradient(ctx, w, h, '#c8323f', '#7d1a2b');
      ctx.fillStyle = 'rgba(31,110,74,0.9)';
      for (let i = 0; i < 5; i++) tree(ctx, w * (0.05 + i * 0.22 + rand() * 0.04), h, w * (0.06 + rand() * 0.04));
      ctx.strokeStyle = 'rgba(255,255,255,0.75)';
      for (let i = 0; i < 26; i++) snowflake(ctx, rand() * w, rand() * h * 0.85, (0.008 + rand() * 0.018) * w);
      break;
    }
    case 'tet-blossom': {
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#d7262e');
      g.addColorStop(1, '#f2642a');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < 22; i++) blossom(ctx, rand() * w, rand() * h, (0.015 + rand() * 0.025) * w, '#ffd84a', '#c2410c');
      break;
    }
    case 'valentine-letters': {
      softGradient(ctx, w, h, '#ffe1ea', '#ffb3c7');
      for (let i = 0; i < 14; i++) {
        ctx.fillStyle = `rgba(226,59,95,${0.35 + rand() * 0.4})`;
        heart(ctx, rand() * w, rand() * h, (0.015 + rand() * 0.03) * w);
      }
      for (let i = 0; i < 5; i++) envelope(ctx, rand() * w, rand() * h, (0.04 + rand() * 0.03) * w, '#fff6f8', '#e23b5f');
      break;
    }
    case 'member-camera':
    case 'host-camera':
      ctx.fillStyle = '#2b2b2b';
      ctx.fillRect(0, 0, w, h);
      break;
    case 'custom': {
      ctx.fillStyle = '#ddd';
      ctx.fillRect(0, 0, w, h);
      if (custom) drawCover(ctx, custom, w, h);
      break;
    }
  }
}

export function drawCover(ctx: Ctx, img: CanvasImageSource, w: number, h: number) {
  const iw = (img as any).naturalWidth || (img as any).videoWidth || (img as any).width;
  const ih = (img as any).naturalHeight || (img as any).videoHeight || (img as any).height;
  if (!iw || !ih) return;
  const s = Math.max(w / iw, h / ih);
  ctx.drawImage(img, (w - iw * s) / 2, (h - ih * s) / 2, iw * s, ih * s);
}

// ---------------------------------------------------------------- filters

export type FilterId = 'original' | 'soft' | 'kbeauty' | 'milk' | 'bw' | 'film' | 'vivid';

export interface Filter {
  id: FilterId;
  name: string;
  /** CSS/canvas filter string (identical for live preview and final photo) */
  css: string;
  /** optional tint overlay */
  tint?: string;
  grain?: number;
}

export const FILTERS: Filter[] = [
  { id: 'original', name: 'Gốc', css: 'none' },
  { id: 'soft', name: 'Soft', css: 'brightness(1.06) contrast(0.94) saturate(1.05)' },
  { id: 'kbeauty', name: 'Hàn Quốc', css: 'brightness(1.12) contrast(0.9) saturate(0.92)', tint: 'rgba(255,190,205,0.10)' },
  { id: 'milk', name: 'Sữa', css: 'brightness(1.15) contrast(0.82) saturate(0.8)', tint: 'rgba(255,250,240,0.12)' },
  { id: 'bw', name: 'Đen trắng', css: 'grayscale(1) contrast(1.15) brightness(1.05)' },
  { id: 'film', name: 'Film', css: 'sepia(0.25) contrast(1.05) saturate(0.9)', tint: 'rgba(255,170,90,0.06)', grain: 0.08 },
  { id: 'vivid', name: 'Rực rỡ', css: 'saturate(1.35) contrast(1.08)' },
];

export const filterById = (id: FilterId) => FILTERS.find((f) => f.id === id) ?? FILTERS[0];

// ---------------------------------------------------------------- frames

export type LayoutId = 'strip4' | 'grid4' | 'strip3' | 'grid6' | 'polaroid';

export interface FrameLayout {
  id: LayoutId;
  name: string;
  /** output size in px (300 dpi print sizes) */
  width: number;
  height: number;
  /** photo cells, in px */
  cells: { x: number; y: number; w: number; h: number }[];
  /** area reserved for logo/caption */
  footer: { x: number; y: number; w: number; h: number };
}

function stripCells(width: number, n: number, pad: number, gap: number, cellAspect: number, top: number) {
  const w = width - pad * 2;
  const h = Math.round(w / cellAspect);
  return Array.from({ length: n }, (_, i) => ({ x: pad, y: top + i * (h + gap), w, h }));
}

function gridCells(cols: number, rows: number, width: number, pad: number, gap: number, cellAspect: number, top: number) {
  const w = Math.round((width - pad * 2 - gap * (cols - 1)) / cols);
  const h = Math.round(w / cellAspect);
  const cells = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) cells.push({ x: pad + c * (w + gap), y: top + r * (h + gap), w, h });
  return cells;
}

/** v0.8: portrait 3:4 — the phone is held upright and the room owner's camera frame is the backdrop */
const STAGE_ASPECT = 3 / 4;

function build(id: LayoutId, name: string, width: number, cells: FrameLayout['cells'], footerH: number): FrameLayout {
  const last = cells[cells.length - 1];
  const height = last.y + last.h + footerH;
  return { id, name, width, height, cells, footer: { x: 0, y: last.y + last.h, w: width, h: footerH } };
}

/** All layouts use the stage aspect (3:4) for every cell, so WYSIWYG holds. */
export const LAYOUTS: FrameLayout[] = [
  build('strip4', 'Dải 4 ảnh', 600, stripCells(600, 4, 36, 22, STAGE_ASPECT, 36), 200),
  build('strip3', 'Dải 3 ảnh', 600, stripCells(600, 3, 36, 22, STAGE_ASPECT, 36), 220),
  build('grid4', 'Lưới 2×2', 1200, gridCells(2, 2, 1200, 44, 24, STAGE_ASPECT, 44), 260),
  build('grid6', 'Lưới 2×3', 1200, gridCells(2, 3, 1200, 44, 22, STAGE_ASPECT, 44), 220),
  build('polaroid', 'Polaroid', 1000, [{ x: 50, y: 50, w: 900, h: 1200 }], 260),
];

export const layoutById = (id: LayoutId) => LAYOUTS.find((l) => l.id === id) ?? LAYOUTS[0];

export type FrameColorId = 'white' | 'black' | 'pink' | 'blue' | 'lavender' | 'mint' | 'butter' | 'check'
  | 'halloween-night' | 'halloween-pumpkin' | 'xmas-red' | 'xmas-green' | 'tet-red' | 'tet-gold' | 'valentine-rose' | 'valentine-cream';

export type StickerSet = 'classic' | 'halloween' | 'xmas' | 'tet' | 'valentine';

export interface FrameColor {
  id: FrameColorId;
  name: string;
  bg: string;
  fg: string;
  /** frame paper decoration */
  pattern?: 'check' | 'dots' | 'stripes';
  patternColor?: string;
  /** margin stickers drawn when "Sticker trang trí" is on */
  sticker?: StickerSet;
  pack?: PackId;
}

export const FRAME_COLORS: FrameColor[] = [
  { id: 'white', name: 'Trắng', bg: '#ffffff', fg: '#222222' },
  { id: 'black', name: 'Đen', bg: '#111111', fg: '#f5f5f5' },
  { id: 'pink', name: 'Hồng', bg: '#ffc9d6', fg: '#7a2f45' },
  { id: 'blue', name: 'Xanh', bg: '#bcd9f5', fg: '#23466b' },
  { id: 'lavender', name: 'Tím', bg: '#d9ccf3', fg: '#43306e' },
  { id: 'mint', name: 'Mint', bg: '#c7ecdc', fg: '#245a45' },
  { id: 'butter', name: 'Vàng', bg: '#fbeaa8', fg: '#6b5413' },
  { id: 'check', name: 'Caro', bg: '#ffe0ea', fg: '#7a2f45', pattern: 'check', patternColor: '#ffffff' },
];

// ---------------------------------------------------------------- seasonal packs (F5)

export type PackId = 'halloween-2026' | 'xmas-2026' | 'tet-2027' | 'valentine-2027';

export interface FramePack {
  id: PackId;
  name: string;
  emoji: string;
  /** first and last day (inclusive), Asia/Ho_Chi_Minh */
  from: string;
  until: string;
  frameColors: FrameColorId[];
  backgrounds: BackgroundId[];
  sticker: StickerSet;
}

export const SEASONAL_FRAME_COLORS: FrameColor[] = [
  { id: 'halloween-night', name: 'Đêm ma', bg: '#2a1846', fg: '#ffb35c', pattern: 'dots', patternColor: '#3d2462', sticker: 'halloween', pack: 'halloween-2026' },
  { id: 'halloween-pumpkin', name: 'Bí ngô', bg: '#ff9a3c', fg: '#3a1d0b', pattern: 'stripes', patternColor: '#ffab57', sticker: 'halloween', pack: 'halloween-2026' },
  { id: 'xmas-red', name: 'Đỏ Noel', bg: '#c8323f', fg: '#fff7ee', pattern: 'dots', patternColor: '#d84a56', sticker: 'xmas', pack: 'xmas-2026' },
  { id: 'xmas-green', name: 'Thông xanh', bg: '#1f6e4a', fg: '#fff7ee', pattern: 'stripes', patternColor: '#26805a', sticker: 'xmas', pack: 'xmas-2026' },
  { id: 'tet-red', name: 'Đỏ Tết', bg: '#d7262e', fg: '#ffe08a', pattern: 'dots', patternColor: '#e23e3e', sticker: 'tet', pack: 'tet-2027' },
  { id: 'tet-gold', name: 'Vàng mai', bg: '#ffd84a', fg: '#9b1c1c', pattern: 'stripes', patternColor: '#ffe27a', sticker: 'tet', pack: 'tet-2027' },
  { id: 'valentine-rose', name: 'Hồng thư', bg: '#ff9db5', fg: '#7a1631', pattern: 'dots', patternColor: '#ffb3c6', sticker: 'valentine', pack: 'valentine-2027' },
  { id: 'valentine-cream', name: 'Kem dâu', bg: '#fff1f4', fg: '#c2185b', pattern: 'check', patternColor: '#ffe0e8', sticker: 'valentine', pack: 'valentine-2027' },
];

export const SEASONAL_BACKGROUNDS: Background[] = [
  { id: 'halloween-moon', name: 'Trăng Halloween', swatch: '#3d2462', pack: 'halloween-2026' },
  { id: 'xmas-snow', name: 'Tuyết Noel', swatch: '#c8323f', pack: 'xmas-2026' },
  { id: 'tet-blossom', name: 'Hoa mai', swatch: '#e2442c', pack: 'tet-2027' },
  { id: 'valentine-letters', name: 'Thư tình', swatch: '#ffb3c7', pack: 'valentine-2027' },
];

export const PACKS: FramePack[] = [
  { id: 'halloween-2026', name: 'Halloween', emoji: '🎃', from: '2026-10-01', until: '2026-11-07',
    frameColors: ['halloween-night', 'halloween-pumpkin'], backgrounds: ['halloween-moon'], sticker: 'halloween' },
  { id: 'xmas-2026', name: 'Giáng sinh', emoji: '🎄', from: '2026-12-01', until: '2027-01-05',
    frameColors: ['xmas-red', 'xmas-green'], backgrounds: ['xmas-snow'], sticker: 'xmas' },
  { id: 'tet-2027', name: 'Tết Đinh Mùi', emoji: '🧧', from: '2027-01-20', until: '2027-02-20',
    frameColors: ['tet-red', 'tet-gold'], backgrounds: ['tet-blossom'], sticker: 'tet' },
  { id: 'valentine-2027', name: 'Valentine', emoji: '💌', from: '2027-02-01', until: '2027-02-20',
    frameColors: ['valentine-rose', 'valentine-cream'], backgrounds: ['valentine-letters'], sticker: 'valentine' },
];

/** Calendar day (YYYY-MM-DD) in Vietnam (UTC+7, no DST). */
export function vnDate(now: Date = new Date()): string {
  return new Date(now.getTime() + 7 * 3600_000).toISOString().slice(0, 10);
}

const dayNum = (d: string) => Math.round(Date.parse(`${d}T00:00:00Z`) / 86_400_000);

/** Days left including today (1 on the last day), or 0 when the pack is not open. */
export function packDaysLeft(p: FramePack, now: Date = new Date()): number {
  const today = vnDate(now);
  if (today < p.from || today > p.until) return 0;
  return dayNum(p.until) - dayNum(today) + 1;
}

export const activePacks = (now: Date = new Date()) => PACKS.filter((p) => packDaysLeft(p, now) > 0);
export const packById = (id: PackId) => PACKS.find((p) => p.id === id);

/** Any id renders – including expired packs (old photos must look the same forever). */
export const frameColorById = (id: FrameColorId): FrameColor =>
  FRAME_COLORS.find((c) => c.id === id) ?? SEASONAL_FRAME_COLORS.find((c) => c.id === id) ?? FRAME_COLORS[0];

/** Frame paper: colour + optional pattern. `k` = render scale. */
export function drawFramePaper(ctx: Ctx, fc: FrameColor, w: number, h: number, k = 1) {
  ctx.fillStyle = fc.bg;
  ctx.fillRect(0, 0, w, h);
  if (!fc.pattern) return;
  ctx.save();
  ctx.fillStyle = fc.patternColor ?? '#ffffff';
  if (fc.pattern === 'check') {
    const s = 24 * k;
    for (let y = 0; y < h; y += s)
      for (let x = Math.round(y / s) % 2 ? s : 0; x < w; x += 2 * s) ctx.fillRect(x, y, s, s);
  } else if (fc.pattern === 'dots') {
    const s = 28 * k;
    for (let y = s / 2, row = 0; y < h; y += s, row++)
      for (let x = row % 2 ? s : s / 2; x < w; x += s) {
        ctx.beginPath();
        ctx.arc(x, y, 4.5 * k, 0, Math.PI * 2);
        ctx.fill();
      }
  } else {
    const s = 22 * k;
    for (let x = -h; x < w; x += s * 2) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x + s, 0);
      ctx.lineTo(x + s + h, h);
      ctx.lineTo(x + h, h);
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.restore();
}

/** Margin stickers for a sticker set (vector shapes: identical on every device). */
export function drawStickerSet(ctx: Ctx, set: StickerSet, w: number, h: number, color: string) {
  let seed = 3;
  const r = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
  ctx.save();
  ctx.globalAlpha = set === 'classic' ? 0.8 : 0.92;
  for (let i = 0; i < 8; i++) {
    const size = w * (0.016 + r() * 0.014);
    const onLeft = i % 2 === 0;
    const x = onLeft ? w * 0.03 + r() * w * 0.025 : w * 0.945 + r() * w * 0.025;
    const y = h * (0.05 + r() * 0.85);
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    switch (set) {
      case 'halloween':
        if (i % 3 === 0) { ctx.fillStyle = '#ff8a2a'; pumpkin(ctx, x, y, size * 1.1); } else if (i % 3 === 1) bat(ctx, x, y, size * 1.3);
        else star(ctx, x, y, size);
        break;
      case 'xmas':
        if (i % 3 === 0) { ctx.fillStyle = '#2f9e6a'; tree(ctx, x, y + size * 1.2, size * 1.1); } else snowflake(ctx, x, y, size * 1.1);
        break;
      case 'tet':
        if (i % 2 === 0) blossom(ctx, x, y, size * 1.1, '#ffd84a', '#c2410c');
        else envelope(ctx, x, y, size * 1.6, '#e0262e', '#ffd84a');
        break;
      case 'valentine':
        if (i % 3 === 1) envelope(ctx, x, y, size * 1.6, '#fff6f8', color);
        else heart(ctx, x, y - size * 0.5, size);
        break;
      default:
        if (i % 3 === 1) star(ctx, x, y, size * 1.2);
        else heart(ctx, x, y - size * 0.5, size);
    }
  }
  ctx.restore();
}

// ---- seasonal shapes

export function pumpkin(ctx: Ctx, x: number, y: number, r: number) {
  const fill = ctx.fillStyle;
  for (const dx of [-0.45, 0.45, 0]) {
    ctx.beginPath();
    ctx.ellipse(x + dx * r, y, r * 0.62, r * 0.78, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = '#3f6b2a';
  ctx.fillRect(x - r * 0.1, y - r * 1.05, r * 0.2, r * 0.32);
  ctx.fillStyle = fill;
}

export function bat(ctx: Ctx, x: number, y: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x, y + r * 0.2);
  ctx.quadraticCurveTo(x - r * 0.5, y - r * 0.5, x - r * 1.2, y - r * 0.2);
  ctx.quadraticCurveTo(x - r * 0.9, y + r * 0.05, x - r * 0.95, y + r * 0.35);
  ctx.quadraticCurveTo(x - r * 0.6, y + r * 0.1, x - r * 0.4, y + r * 0.45);
  ctx.quadraticCurveTo(x - r * 0.2, y + r * 0.2, x, y + r * 0.55);
  ctx.quadraticCurveTo(x + r * 0.2, y + r * 0.2, x + r * 0.4, y + r * 0.45);
  ctx.quadraticCurveTo(x + r * 0.6, y + r * 0.1, x + r * 0.95, y + r * 0.35);
  ctx.quadraticCurveTo(x + r * 0.9, y + r * 0.05, x + r * 1.2, y - r * 0.2);
  ctx.quadraticCurveTo(x + r * 0.5, y - r * 0.5, x, y + r * 0.2);
  ctx.fill();
}

export function snowflake(ctx: Ctx, x: number, y: number, r: number) {
  ctx.save();
  ctx.lineWidth = Math.max(1, r * 0.14);
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i;
    const cx = Math.cos(a), cy = Math.sin(a);
    ctx.moveTo(x, y);
    ctx.lineTo(x + cx * r, y + cy * r);
    const bx = x + cx * r * 0.6, by = y + cy * r * 0.6;
    ctx.moveTo(bx, by);
    ctx.lineTo(bx + Math.cos(a + 0.6) * r * 0.3, by + Math.sin(a + 0.6) * r * 0.3);
    ctx.moveTo(bx, by);
    ctx.lineTo(bx + Math.cos(a - 0.6) * r * 0.3, by + Math.sin(a - 0.6) * r * 0.3);
  }
  ctx.stroke();
  ctx.restore();
}

/** Christmas tree anchored at (x, bottom). */
export function tree(ctx: Ctx, x: number, bottom: number, r: number) {
  for (let i = 0; i < 3; i++) {
    const top = bottom - r * (1.1 + i * 0.75);
    const half = r * (0.95 - i * 0.22);
    ctx.beginPath();
    ctx.moveTo(x, top - r * 0.6);
    ctx.lineTo(x - half, top + r * 0.55);
    ctx.lineTo(x + half, top + r * 0.55);
    ctx.closePath();
    ctx.fill();
  }
}

/** Five-petal apricot blossom (hoa mai). */
export function blossom(ctx: Ctx, x: number, y: number, r: number, petal: string, center: string) {
  const prev = ctx.fillStyle;
  ctx.fillStyle = petal;
  for (let i = 0; i < 5; i++) {
    const a = (Math.PI * 2 / 5) * i - Math.PI / 2;
    ctx.beginPath();
    ctx.ellipse(x + Math.cos(a) * r * 0.55, y + Math.sin(a) * r * 0.55, r * 0.48, r * 0.36, a, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = center;
  ctx.beginPath();
  ctx.arc(x, y, r * 0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = prev;
}

/** Envelope (love letter / lì xì), centred. */
export function envelope(ctx: Ctx, x: number, y: number, w: number, paper: string, ink: string) {
  const h = w * 0.66;
  const prev = ctx.fillStyle;
  ctx.fillStyle = paper;
  ctx.fillRect(x - w / 2, y - h / 2, w, h);
  ctx.strokeStyle = ink;
  ctx.lineWidth = Math.max(1, w * 0.05);
  ctx.strokeRect(x - w / 2, y - h / 2, w, h);
  ctx.beginPath();
  ctx.moveTo(x - w / 2, y - h / 2);
  ctx.lineTo(x, y + h * 0.08);
  ctx.lineTo(x + w / 2, y - h / 2);
  ctx.stroke();
  ctx.fillStyle = ink;
  heart(ctx, x, y - h * 0.02, w * 0.1);
  ctx.fillStyle = prev;
}

export const STAGE_W = 720;
export const STAGE_H = 960;
